import { useMemo } from 'react';
import { SMS, TaskStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { useTranslation } from '@frontend/i18n';
import { useTaskCitation } from '@frontend/task-center-scope';
import { Chat, Chip, useAlert } from '@frontend/design-system';

const getSortedTasksByStatus = (tasks: SMS['tasks'], status: TaskStatus) =>
  tasks!.filter((task) => task.status === status).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

const getTasksData = (tasks: SMS['tasks']) => {
  if (!tasks || tasks.length === 0) {
    return undefined;
  }

  const toDoTasks = getSortedTasksByStatus(tasks, TaskStatus.TASK_STATUS_TO_DO);
  const inProgressTasks = getSortedTasksByStatus(tasks, TaskStatus.TASK_STATUS_IN_PROGRESS);
  const doneTasks = getSortedTasksByStatus(tasks, TaskStatus.TASK_STATUS_DONE);

  return {
    latestToDoTaskId: toDoTasks[0]?.taskId,
    latestInProgressTaskId: inProgressTasks[0]?.taskId,
    latestDoneTaskId: doneTasks[0]?.taskId,
    toDoCount: toDoTasks.length,
    inProgressCount: inProgressTasks.length,
    doneCount: doneTasks.length,
  };
};

type SMSTaskProps = {
  tasks: SMS['tasks'];
};

export const SMSTask = ({ tasks }: SMSTaskProps) => {
  const { t } = useTranslation('thread-body');
  const alert = useAlert();
  const { openTaskWithTaskId } = useTaskCitation();

  const taskData = useMemo(() => getTasksData(tasks), [tasks]);

  if (!taskData) {
    return null;
  }

  const { toDoCount, inProgressCount, doneCount, latestToDoTaskId, latestInProgressTaskId, latestDoneTaskId } =
    taskData;

  const onClickTask = (latestTaskId?: string) => {
    if (!latestTaskId) {
      alert.error(t('No tasks found'));
      return;
    }
    openTaskWithTaskId(latestTaskId);
  };

  return (
    <>
      {toDoCount > 0 && (
        <Chip.Task tasks={toDoCount > 1 ? toDoCount : 0} variant='task' onClick={() => onClickTask(latestToDoTaskId)}>
          {t('To Do')}
        </Chip.Task>
      )}
      {inProgressCount > 0 && (
        <Chip.Task
          tasks={inProgressCount > 1 ? inProgressCount : 0}
          variant='task'
          onClick={() => onClickTask(latestInProgressTaskId)}
        >
          {t('In Progress')}
        </Chip.Task>
      )}
      {doneCount > 0 && (
        <Chip.Task tasks={doneCount > 1 ? doneCount : 0} variant='done' onClick={() => onClickTask(latestDoneTaskId)}>
          {t('Done')}
        </Chip.Task>
      )}
    </>
  );
};

SMSTask.displayName = Chat.Tag.displayName;
